import { useState, useEffect } from "react";
import NoAccess from "../../../../../../assets/img/NoAccess.png";
import { Form, Button, Modal, Table } from "react-bootstrap";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import BASE_URL from "../../../../../../assets/global/url";
import { constant_productCategory } from "../../../../../../constants/productOptions";
import swal from "sweetalert";
import imageCompression from "browser-image-compression";
import productUnits from "../../../../../../assets/global/unitofmeasure";
import useDecodeToken from "../../../../../../hooks/customHook/useDecodeToken";
import { selectCustomStyles } from "../../../../../../assets/global/selectCustomStyles";
import {
  usePagination,
  PaginationControls,
} from "../../../../../../hooks/customHook/paginationHook/usePagination";
import { useForm, useFieldArray, Controller, useWatch } from "react-hook-form";
import Select from "react-select";
import NumberInDecimals from "../../../../../../components/inputs/NumberFormatted";
import { parseNumber } from "../../../../../../utils/numberFormatter";

const NewUpdateProduct = ({ authrztn }) => {
  const userLoggedID = useDecodeToken();
  const { id } = useParams();
  const navigate = useNavigate();

  // State Management
  const [existingImages, setExistingImages] = useState([]);
  const [images, setImages] = useState([]);
  const [removeImageIds, setRemoveImageIds] = useState([]);
  const [deletedVendorIds, setDeletedVendorIds] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [validated, setValidated] = useState(false);

  // Modal State
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [modalImages, setModalImages] = useState([]);

  const MAX_IMAGE_SIZE_MB = 5;
  const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

  // React Hook Form Setup
  const { register, control, setValue, getValues, watch, trigger } = useForm({
    defaultValues: {
      productCode: "",
      productName: "",
      productCategory: "",
      productUnit: "",
      remarks: "",
      productThreshold: "",
      items: [],
    },
  });

  const {
    fields: vendortable,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "items",
  });

  const pagination = usePagination(vendortable, 10);
  const currentItemsWatch = useWatch({ control, name: "items" });

  // Fetch Product General Info
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const productRes = await axios.get(
          `${BASE_URL}/product/fetchProductEdit`,
          { params: { id } },
        );

        if (productRes.data.length > 0) {
          const product = productRes.data[0];
          setValue("productCode", product.product_code);
          setValue("productName", product.product_name);
          setValue("productCategory", product.product_category);
          setValue("productUnit", product.unit_of_measure);
          setValue("remarks", product.description);
          setValue("productThreshold", product.threshold);
        }
      } catch (err) {
        console.log("Error fetching product data:", err);
      }
    };
    if (id) fetchProduct();
  }, [id, setValue]);

  //  Fetch Supplier List (For Dropdown)
  useEffect(() => {
    axios
      .get(`${BASE_URL}/product/getVendors`)
      .then((res) => {
        setVendorList(res.data || []);
      })
      .catch((err) => console.log(err));
  }, []);

  // Fetch Linked Suppliers
  useEffect(() => {
    const fetchLinkedSuppliers = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/productTagVendor/fetchProductVendor`,
          {
            params: { id: id, limit: 10000, page: 1 },
          },
        );

        if (res.data && res.data.data && res.data.data.length > 0) {
          const initialItems = res.data.data.map((vendor) => {
            const fName = vendor.vendor.fname || "";
            const lName = vendor.vendor.lname || "";
            const fullName = `${fName} ${lName}`.trim();
            const finalVendorName =
              fullName || vendor.vendor.company_name || "---";

            return {
              producttagId: vendor.id,
              companyName: vendor.vendor.company_name || "---",
              vendorId: String(vendor.vendor.id),
              vendorName: finalVendorName,
              vendorEmail: vendor.vendor.company_email || "---",
              vendorCountry: vendor.vendor.company_country || "---",
              vendorPrice: vendor.product_price,
              newVendor: false,
            };
          });
          replace(initialItems);
        }
      } catch (error) {
        console.error("Error fetching linked vendors:", error);
      }
    };
    if (id) fetchLinkedSuppliers();
  }, [id, replace]);

  // Fetch All Product Data
  const loadProductData = async () => {
    if (!id) return;

    // 1. Fetch Product General Info
    try {
      const productRes = await axios.get(
        `${BASE_URL}/product/fetchProductEdit`,
        { params: { id } },
      );

      if (productRes.data.length > 0) {
        const product = productRes.data[0];
        setValue("productCode", product.product_code);
        setValue("productName", product.product_name);
        setValue("productCategory", product.product_category);
        setValue("productUnit", product.unit_of_measure);
        setValue("remarks", product.description);
        setValue("productThreshold", product.threshold);
      }
    } catch (err) {
      console.log("Error fetching product data:", err);
    }

    // 2. Fetch Linked Suppliers
    try {
      const res = await axios.get(
        `${BASE_URL}/productTagVendor/fetchProductVendor`,
        {
          params: { id: id, limit: 10000, page: 1 },
        },
      );

      if (res.data && res.data.data && res.data.data.length > 0) {
        const initialItems = res.data.data.map((vendor) => {
          const fName = vendor.vendor.fname || "";
          const lName = vendor.vendor.lname || "";
          const fullName = `${fName} ${lName}`.trim();
          const finalVendorName =
            fullName || vendor.vendor.company_name || "---";

          return {
            producttagId: vendor.id,
            companyName: vendor.vendor.company_name || "---",
            vendorId: String(vendor.vendor.id),
            vendorName: finalVendorName,
            vendorEmail: vendor.vendor.company_email || "---",
            vendorCountry: vendor.vendor.company_country || "---",
            vendorPrice: vendor.product_price,
            newVendor: false,
          };
        });
        replace(initialItems);
      }
    } catch (error) {
      console.error("Error fetching linked vendors:", error);
    }

    // 3. Fetch Existing Images
    try {
      const res = await axios.get(`${BASE_URL}/product/product-images/${id}`);
      setExistingImages(res.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    loadProductData();
  }, [id, setValue, replace]);

  // 4. Fetch Supplier List (For Dropdown)
  useEffect(() => {
    axios
      .get(`${BASE_URL}/product/getVendors`)
      .then((res) => {
        setVendorList(res.data || []);
      })
      .catch((err) => console.log(err));
  }, []);

  // Fetch Existing Images
  useEffect(() => {
    axios
      .get(`${BASE_URL}/product/product-images/${id}`)
      .then((res) => {
        setExistingImages(res.data || []);
      })
      .catch((err) => console.log(err));
  }, [id]);

  // --- Handlers ---
  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    const compressedImages = [];
    for (const file of files) {
      try {
        const options = {
          maxSizeMB: MAX_IMAGE_SIZE_MB,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };
        const compressedFile = await imageCompression(file, options);

        if (compressedFile.size > MAX_IMAGE_SIZE_BYTES) {
          swal({
            icon: "error",
            title: "File Too Large",
            text: `The file "${file.name}" exceeds the ${MAX_IMAGE_SIZE_MB} MB limit.`,
          });
          return;
        }
        compressedImages.push(compressedFile);
      } catch (err) {
        swal({
          icon: "error",
          title: "Compression Error",
          text: `Failed to compress "${file.name}".`,
        });
        return;
      }
    }
    setImages(compressedImages);
  };

  const handleViewImages = () => {
    const allImages = [
      ...existingImages.map((img) => ({
        type: "existing",
        id: img.id,
        src: `data:image/jpeg;base64,${img.product_image}`,
        label: `Existing #${img.id}`,
      })),
      ...images.map((img, idx) => ({
        type: "new",
        id: idx,
        src: URL.createObjectURL(img),
        label: img.name || `New #${idx + 1}`,
      })),
    ];
    setModalImages(allImages);
    setCurrentImageIndex(0);
    setShowImageModal(true);
  };

  const handlePrevImage = () =>
    setCurrentImageIndex((prev) =>
      prev === 0 ? modalImages.length - 1 : prev - 1,
    );
  const handleNextImage = () =>
    setCurrentImageIndex((prev) =>
      prev === modalImages.length - 1 ? 0 : prev + 1,
    );

  const addSupplierRow = () => {
    const currentItems = getValues("items") || [];

    if (
      currentItems.length > 0 &&
      currentItems.some((item) => !item.vendorId)
    ) {
      swal({
        icon: "warning",
        title: "Supplier Required",
        text: "Please select a supplier for the existing rows before adding a new item.",
      });
      return;
    }

    append({
      producttagId: "",
      companyName: "",
      vendorId: "",
      vendorName: "",
      vendorEmail: "",
      vendorCountry: "",
      vendorPrice: 0,
      newVendor: true,
    });

    setTimeout(() => {
      if (
        pagination.totalPages > 1 &&
        pagination.currentPage !== pagination.totalPages
      ) {
        pagination.lastPage();
      }
    }, 50);
  };

  const removeSupplierRow = (actualIndex) => {
    const removedItem = getValues(`items.${actualIndex}`);

    if (removedItem && removedItem.vendorId && !removedItem.newVendor) {
      setDeletedVendorIds((prev) => [...prev, removedItem.vendorId]);
    } else if (removedItem && removedItem.vendorId) {
      setDeletedVendorIds((prev) => [...prev, removedItem.vendorId]);
    }
    remove(actualIndex);
  };

  const update = async () => {
    try {
      const formData = getValues();
      const currentItems = formData.items;

      const formattedFormData = {
        ...formData,
        productThreshold: parseNumber(formData.productThreshold),
        items: currentItems.map((item) => {
          let pPrice = parseNumber(item.vendorPrice) || 0;
          if (pPrice === 0) pPrice = 1;

          return { ...item, vendorPrice: pPrice };
        }),
      };

      const data = new FormData();
      data.append("id", id);
      data.append("userLoggedID", userLoggedID);
      data.append("deletedVendorIdList", deletedVendorIds.join(","));

      if (removeImageIds.length > 0) {
        data.append("removeImageIds", JSON.stringify(removeImageIds));
      }

      Object.entries(formattedFormData).forEach(([key, value]) => {
        if (key === "items") {
          data.append(key, JSON.stringify(value));
        } else {
          data.append(key, value);
        }
      });
      images.forEach((img) => data.append("images", img));

      await axios.put(`${BASE_URL}/product/updateProduct`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      swal({
        icon: "success",
        title: "Updated",
        text: "Product successfully updated!",
        timer: 1500,
        buttons: false,
      });

      setTimeout(() => {
        // Refetch the updated data instead of navigating away
        loadProductData();

        // Clear staging states to prevent resending deletions/additions
        setImages([]);
        setRemoveImageIds([]);
        setDeletedVendorIds([]);
      }, 1500);
    } catch (err) {
      console.error(err);
      if (err.response && err.response.status === 409) {
        swal({
          icon: "error",
          title: err.response.data.message,
          text: "Please choose a unique name for the product to proceed.",
        });
      } else {
        swal({
          icon: "error",
          title: "Update Failed",
          text: "Please try again.",
        });
      }
    }
  };

  const confirmSave = async (e) => {
    e.preventDefault();
    const isValid = await trigger();

    if (!isValid) {
      setValidated(true);
      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill in all the required text fields.",
      });
      return;
    }

    const currentItems = getValues("items") || [];

    if (
      currentItems.length === 0 ||
      currentItems.some((item) => !item.companyName)
    ) {
      swal({
        icon: "warning",
        title: "Supplier Required",
        text: "Please select at least one supplier before continuing.",
      });
      return;
    }

    const hasZeroPrice = currentItems.some(
      (item) => parseNumber(item.vendorPrice) === 0 || !item.vendorPrice,
    );

    if (hasZeroPrice) {
      await swal({
        icon: "info",
        title: "Price Adjusted",
        text: "Any supplier price set to 0 will automatically be adjusted to 1.",
      });
    }

    swal({
      title: "Save changes?",
      text: "This will update the product.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willSave) => {
      if (willSave) update();
    });
    setValidated(true);
  };

  // Helper to get dynamically selected IDs for the dropdown filter
  const currentSelectedIds = (currentItemsWatch || []).map((item) =>
    String(item.vendorId),
  );

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      {authrztn.includes("ProductList-Edit") ? (
        <Form noValidate validated={validated} onSubmit={confirmSave}>
          <div className="w-100 p-2 d-flex flex-row justify-content-between">
            <div className="d-flex flex-column title-custom">
              <span className="fs-3">
                <Link to="/inventory/product-list" className="text-dark me-2">
                  <i className="fa-solid fa-arrow-left"></i>
                </Link>
                UPDATE PRODUCT
              </span>
            </div>
          </div>

          <div className="p-2">
            <span className="text-decoration-underline">
              GENERAL INFORMATION
            </span>

            <div className="row mb-3 mt-3">
              <div className="col-md-6">
                <Form.Label htmlFor="productCode">Product Code</Form.Label>
                <Form.Control
                  type="text"
                  className="p-3"
                  id="productCode"
                  {...register("productCode")}
                  readOnly
                />
              </div>
              <div className="col-md-6">
                <Form.Label htmlFor="productName">
                  Product Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  className="p-3"
                  placeholder="Enter Item Name"
                  id="productName"
                  {...register("productName", { required: true })}
                  required
                />
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Label htmlFor="productUnit">
                  Unit of Measure <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  className="form-select p-3"
                  id="productUnit"
                  {...register("productUnit", { required: true })}
                  required
                >
                  <option disabled value="">
                    Select Unit
                  </option>
                  {productUnits.map((unit, index) => (
                    <option key={index} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-6">
                <Form.Label htmlFor="productCategory">
                  Product Category <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  className="p-3"
                  {...register("productCategory", { required: true })}
                  required
                >
                  <option disabled value="">
                    Select Category
                  </option>
                  {constant_productCategory.map((category, index) => (
                    <option key={index} value={category}>
                      {category}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Label htmlFor="productImages">Product Images</Form.Label>
                <Form.Control
                  type="file"
                  id="productImages"
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                />

                <div className="mt-2">
                  {images.length > 0 &&
                    Array.from(images).map((img, idx) => (
                      <span key={idx} className="badge bg-info me-1">
                        {img.name}
                      </span>
                    ))}
                </div>

                <div className="mt-2">
                  {existingImages.length > 0 &&
                    existingImages.map((img) => (
                      <span key={img.id} className="badge bg-secondary me-1">
                        <img
                          src={`data:image/jpeg;base64,${img.product_image}`}
                          alt="Product"
                          style={{
                            width: 40,
                            height: 40,
                            objectFit: "cover",
                            marginRight: 4,
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger ms-1"
                          onClick={() => {
                            setRemoveImageIds((prev) => [...prev, img.id]);
                            setExistingImages((prev) =>
                              prev.filter((i) => i.id !== img.id),
                            );
                          }}
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                </div>

                {/* View Images Button */}
                {(existingImages.length > 0 || images.length > 0) && (
                  <Button
                    variant="info"
                    size="sm"
                    className="mt-2"
                    onClick={handleViewImages}
                  >
                    View Images
                  </Button>
                )}
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-6">
                <Form.Label htmlFor="remarks">Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  {...register("remarks")}
                  style={{
                    fontSize: "16px",
                    height: "200px",
                    resize: "none",
                    overflowY: "auto",
                  }}
                />
              </div>
            </div>

            <span className="text-decoration-underline">
              THRESHOLD NOTIFICATION
            </span>
            <div className="row mb-3 mt-3">
              <div className="col-md-6">
                <Form.Label htmlFor="productThreshold">
                  Critical Inventory Threshold{" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <div className="input-group">
                  <span className="input-group-text p-3">No.</span>
                  <Controller
                    name="productThreshold"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <NumberInDecimals
                        className="form-control p-3"
                        placeholder="0.00"
                        decimals={2}
                        value={field.value}
                        onChange={field.onChange}
                        required
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          <Table bordered hover responsive>
            <thead className="table-light">
              <tr>
                <th className="p-2">
                  Company Name <span className="text-danger">*</span>
                </th>
                <th className="p-2">Contact Person</th>
                <th className="p-2">Company Email</th>
                <th className="p-2">Country</th>
                <th className="p-2">Price</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {pagination.paginatedData.map((item, localIndex) => {
                const actualIndex =
                  (pagination.currentPage - 1) * pagination.itemsPerPage +
                  localIndex;

                return (
                  <tr key={item.id}>
                    <td>
                      <Controller
                        name={`items.${actualIndex}.vendorId`}
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => {
                          const vendorOptions = (
                            vendortable.length === 1
                              ? vendorList
                              : vendorList.filter(
                                  (data) =>
                                    !currentSelectedIds.includes(
                                      String(data.id),
                                    ) ||
                                    String(field.value) === String(data.id),
                                )
                          ).map((data) => ({
                            value: String(data.id),
                            label:
                              data.company_name ||
                              `${data.fname} ${data.lname}`,
                          }));

                          return (
                            <Select
                              {...field}
                              options={vendorOptions}
                              value={
                                vendorOptions.find(
                                  (opt) => opt.value === String(field.value),
                                ) || null
                              }
                              onChange={(selectedOption) => {
                                const newVendorId = selectedOption?.value;
                                const oldVendorId = field.value;

                                if (
                                  oldVendorId &&
                                  oldVendorId !== newVendorId &&
                                  !getValues(`items.${actualIndex}.newVendor`)
                                ) {
                                  setDeletedVendorIds((prev) => [
                                    ...prev,
                                    oldVendorId,
                                  ]);
                                }

                                field.onChange(newVendorId);

                                const selectedVendor = vendorList.find(
                                  (v) => String(v.id) === String(newVendorId),
                                );

                                if (selectedVendor) {
                                  const fName = selectedVendor.fname || "";
                                  const lName = selectedVendor.lname || "";
                                  const fullName = `${fName} ${lName}`.trim();

                                  setValue(
                                    `items.${actualIndex}.companyName`,
                                    selectedVendor.company_name || "---",
                                  );
                                  setValue(
                                    `items.${actualIndex}.vendorName`,
                                    fullName ||
                                      selectedVendor.company_name ||
                                      "---",
                                  );
                                  setValue(
                                    `items.${actualIndex}.vendorEmail`,
                                    selectedVendor.company_email || "---",
                                  );
                                  setValue(
                                    `items.${actualIndex}.vendorCountry`,
                                    selectedVendor.company_country || "---",
                                  );
                                  setValue(
                                    `items.${actualIndex}.newVendor`,
                                    true,
                                  );
                                }
                              }}
                              onMenuOpen={() => {
                                if (vendorList.length === 0) {
                                  swal({
                                    icon: "warning",
                                    title: "No Supplier Found",
                                    text: "No suppliers found. Please create one first.",
                                  });
                                }
                              }}
                              placeholder="Select Supplier"
                              styles={selectCustomStyles(
                                watch(`items.${actualIndex}.vendorId`),
                                validated,
                                "0.12rem",
                              )}
                              isSearchable
                            />
                          );
                        }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        {...register(`items.${actualIndex}.vendorName`)}
                        className="p-2"
                        readOnly
                      />
                    </td>
                    <td>
                      <Form.Control
                        {...register(`items.${actualIndex}.vendorEmail`)}
                        className="p-2"
                        readOnly
                      />
                    </td>
                    <td>
                      <Form.Control
                        {...register(`items.${actualIndex}.vendorCountry`)}
                        className="p-2"
                        readOnly
                      />
                    </td>
                    <td>
                      <Controller
                        name={`items.${actualIndex}.vendorPrice`}
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <NumberInDecimals
                            className="form-control p-2"
                            placeholder="0.00"
                            value={field.value}
                            onChange={field.onChange}
                            decimals={4}
                            required
                          />
                        )}
                      />
                    </td>
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeSupplierRow(actualIndex)}
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <div className="text-end mt-2 p-2">
            <button
              className="btn btn-primary btn-sm"
              type="button"
              onClick={addSupplierRow}
            >
              New Item
            </button>
          </div>

          <div>
            <PaginationControls {...pagination} />
          </div>

          <div className="row mx-auto p-2 mt-4 mb-4"></div>

          <div className="w-100 mb-3 d-flex flex-row align-items-end justify-content-end">
            <button
              className="btn btn-secondary title-button mx-3"
              type="button"
              onClick={() => navigate("/inventory/product-list")}
            >
              Cancel
            </button>
            <button className="btn btn-primary title-button" type="submit">
              Save
            </button>
          </div>
        </Form>
      ) : (
        <div className="no-access">
          <img src={NoAccess} alt="NoAccess" className="no-access-img" />
          <h3>You don't have access to this function.</h3>
        </div>
      )}

      {/* Image Viewer Modal */}
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Image Viewer</Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column align-items-center">
          {modalImages.length > 0 && (
            <>
              <img
                src={modalImages[currentImageIndex].src}
                alt={modalImages[currentImageIndex].label}
                style={{ maxWidth: "100%", maxHeight: "60vh" }}
              />
              <div className="mt-3 d-flex justify-content-between w-100">
                <Button
                  variant="secondary"
                  onClick={handlePrevImage}
                  disabled={modalImages.length <= 1}
                >
                  &lt;
                </Button>
                <span>
                  {currentImageIndex + 1} of {modalImages.length}
                </span>
                <Button
                  variant="secondary"
                  onClick={handleNextImage}
                  disabled={modalImages.length <= 1}
                >
                  &gt;
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default NewUpdateProduct;
